"use client"

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SectionTitle from '../components/SectionTitle';
import CallToAction from '../components/CallToAction';
import ImageSlider from '../components/ImageSlider';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { FaEnvelope } from 'react-icons/fa';

// Add fade-in animation utility
const fadeInClass = "animate-fade-in";

// Professional About Section with card and icon
const AboutBeginningsSection = () => (
  <section className="relative bg-gradient-to-br from-blue-50 via-white to-green-50 py-16 overflow-hidden">
    <div className="absolute top-0 left-0 w-40 h-40 bg-blue-200 rounded-full opacity-30 blur-2xl -z-10" style={{top: '-4rem', left: '-4rem'}} />
    <div className="absolute bottom-0 right-0 w-60 h-60 bg-green-200 rounded-full opacity-20 blur-2xl -z-10" style={{bottom: '-6rem', right: '-6rem'}} />
    <div className="container mx-auto px-4 flex flex-col md:flex-row items-center gap-12">
      <div className="flex-1">
        <h2 className="text-4xl md:text-5xl font-extrabold text-blue-800 mb-4 drop-shadow-lg">Our Journey Begins</h2>
        <p className="text-lg md:text-xl text-gray-700 mb-6">
          Somali Eye Care Organization is a newly established, non-profit initiative dedicated to making quality eye care accessible for all Somalis. Founded by passionate professionals and community members, our mission is to prevent avoidable blindness and restore sight—one person, one community at a time.
        </p>
        <ul className="mb-6 space-y-2">
          <li className="flex items-center gap-2 text-green-700"><span className="text-xl">👁️</span> Community-based outreach</li>
          <li className="flex items-center gap-2 text-blue-700"><span className="text-xl">🤝</span> Building local partnerships</li>
          <li className="flex items-center gap-2 text-yellow-700"><span className="text-xl">🌱</span> Early-stage impact, big vision</li>
        </ul>
        <p className="text-md text-gray-600 mb-4">
          Though our journey has just begun, we are already working to raise awareness, build partnerships, and deliver essential eye care services. Every step brings us closer to a future where no one in Somalia suffers from preventable vision loss.
        </p>
        <Link href="/about" className="inline-block bg-gradient-to-r from-blue-500 to-green-500 text-white font-semibold px-8 py-3 rounded-full shadow-lg hover:scale-105 hover:shadow-2xl transition-transform">
          Learn More
        </Link>
      </div>
      <div className="flex-1 flex justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-10 flex flex-col items-center animate-fade-in">
          <Image src="/assets/img/dsc1.jpg" alt="Eye Care Outreach" width={350} height={250} className="rounded-xl shadow-md mb-4" />
          <span className="text-blue-600 font-bold text-lg">People-Centered Eye Care</span>
        </div>
      </div>
    </div>
  </section>
);

const AwarenessCampaignsSection = () => (
  <section className="relative bg-gradient-to-br from-yellow-50 via-white to-yellow-100 py-16 overflow-hidden">
    <div className="absolute top-0 left-0 w-32 h-32 bg-yellow-200 rounded-full opacity-20 blur-2xl -z-10" style={{top: '-3rem', left: '-3rem'}} />
    <div className="absolute bottom-0 right-0 w-40 h-40 bg-yellow-300 rounded-full opacity-20 blur-2xl -z-10" style={{bottom: '-4rem', right: '-4rem'}} />
    <div className="container mx-auto px-4 text-center">
      <h2 className="text-4xl font-extrabold text-yellow-700 mb-4 drop-shadow-lg flex items-center justify-center gap-2">
        <span>Awareness Campaigns</span> <span className="text-2xl">📢</span>
      </h2>
      <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
        Glaucoma, cataracts, and other eye diseases can lead to preventable blindness. Our campaigns educate the public about early detection and treatment options to save sight.
      </p>
      <a
        href="/campaigns"
        className="inline-block bg-gradient-to-r from-yellow-400 to-yellow-600 text-white font-semibold px-8 py-3 rounded-full shadow-lg hover:scale-105 hover:shadow-2xl transition-transform"
      >
        Learn More
      </a>
    </div>
  </section>
);

const CallToActionSection = () => (
  <section className="relative bg-gradient-to-br from-blue-100 via-white to-blue-200 py-16 overflow-hidden">
    <div className="absolute top-0 left-0 w-32 h-32 bg-blue-200 rounded-full opacity-20 blur-2xl -z-10" style={{top: '-3rem', left: '-3rem'}} />
    <div className="absolute bottom-0 right-0 w-40 h-40 bg-blue-300 rounded-full opacity-20 blur-2xl -z-10" style={{bottom: '-4rem', right: '-4rem'}} />
    <div className="container mx-auto px-4 text-center">
      <h2 className="text-4xl font-extrabold text-blue-700 mb-4 drop-shadow-lg flex items-center justify-center gap-2">
        <span>Make a Difference</span> <span className="text-2xl">🌟</span>
      </h2>
      <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
        Your support can help restore sight and prevent blindness. Join us in making a lasting impact on the lives of those in need.
      </p>
      <div className="flex flex-col md:flex-row justify-center gap-4">
        <a
          href="/donate"
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-8 py-3 rounded-full shadow-lg font-semibold hover:scale-105 hover:shadow-2xl transition-transform"
        >
          Donate Now
        </a>
        <a
          href="/volunteer"
          className="bg-gradient-to-r from-green-500 to-green-700 text-white px-8 py-3 rounded-full shadow-lg font-semibold hover:scale-105 hover:shadow-2xl transition-transform"
        >
          Volunteer
        </a>
      </div>
    </div>
  </section>
);

const VolunteerSection = () => (
  <section className="relative bg-gradient-to-br from-green-50 via-white to-green-100 py-16 overflow-hidden">
    <div className="absolute top-0 left-0 w-32 h-32 bg-green-200 rounded-full opacity-20 blur-2xl -z-10" style={{top: '-3rem', left: '-3rem'}} />
    <div className="absolute bottom-0 right-0 w-40 h-40 bg-green-300 rounded-full opacity-20 blur-2xl -z-10" style={{bottom: '-4rem', right: '-4rem'}} />
    <div className="container mx-auto px-4 text-center">
      <h2 className="text-4xl font-extrabold text-green-700 mb-4 drop-shadow-lg flex items-center justify-center gap-2">
        <span>Become a Volunteer</span> <span className="text-2xl">🙌</span>
      </h2>
      <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
        Join our team of dedicated volunteers and help us bring eye care services to those who need it the most. Your time and skills can make a world of difference.
      </p>
      <a
        href="/volunteer"
        className="inline-block bg-gradient-to-r from-green-500 to-green-700 text-white font-semibold px-8 py-3 rounded-full shadow-lg hover:scale-105 hover:shadow-2xl transition-transform"
      >
        Join Us
      </a>
    </div>
  </section>
);

const ResearchAchievementsSection = () => (
  <section className="bg-purple-100 py-12">
    <div className="container mx-auto px-4 text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Our Research and Achievements</h2>
      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        We have conducted groundbreaking research on eye diseases and provided innovative solutions to combat blindness.
        Our achievements include successful outreach programs, partnerships, and training initiatives for eye care
        professionals.
      </p>
      <a
        href="/achievements"
        className="bg-purple-500 text-white px-6 py-3 rounded-lg shadow-md hover:bg-purple-600 transition"
      >
        Discover More
      </a>
    </div>
  </section>
);

const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleImageClick = (image: string) => {
    setSelectedImage(image);
  };

  const closePopup = () => {
    setSelectedImage(null);
  };

  return (
    <section className="bg-gray-50 py-12">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-3xl font-bold text-gray-800 mb-6">Gallery</h2>
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          Explore moments from our outreach programs, training sessions, and community events. Each picture tells a story
          of hope and transformation.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {["/assets/img/dsc1.jpg", "/assets/img/dsc2.jpg", "/assets/img/dsc3.jpg"].map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`Gallery Image ${index + 1}`}
              className="rounded-lg shadow-md cursor-pointer hover:scale-105 transition-transform"
              onClick={() => handleImageClick(image)}
            />
          ))}
        </div>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg shadow-lg p-4">
            <img
              src={selectedImage}
              alt="Selected"
              className="max-w-full max-h-[80vh] rounded-md"
            />
            <button
              onClick={closePopup}
              className="absolute top-2 right-2 bg-gray-800 text-white rounded-full p-2 shadow-md hover:bg-gray-900 transition-transform transform hover:scale-110"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </section>
  );
};



export default function HomePage() {
  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <div className="relative w-full h-[400px] md:h-[600px] overflow-hidden mb-8">
        <Image
          src="/assets/img/about4.jpeg"
          alt="Preventing Blindness and Restoring Sight"
          layout="fill"
          objectFit="cover"
          className="opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-extrabold mb-4 tracking-wide drop-shadow-lg">Somali Eye Care</h1>
            <p className="text-lg md:text-2xl mb-6 font-light drop-shadow-md">Preventing Blindness and Restoring Sight Through Eye Care Services in Somalia.</p>
            <div className="flex gap-4 justify-center">
              <Link href="/about" className="bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold px-6 py-3 rounded-full shadow-xl hover:scale-105 hover:shadow-2xl transition-transform">
                Learn More
              </Link>
              <Link href="/donate" className="bg-white text-blue-600 font-semibold px-6 py-3 rounded-full shadow-xl hover:bg-gray-100 hover:scale-105 hover:shadow-2xl transition-transform">
                Donate Now
              </Link>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1">
        {/* Introduction Section */}
        <section className="relative bg-gradient-to-br from-blue-100 via-white to-green-100 py-16 overflow-hidden rounded-3xl shadow-2xl mb-12">
          {/* Further reduced and repositioned gradients for no overflow */}
          <div className="absolute top-4 left-4 w-20 h-20 bg-blue-200 rounded-full opacity-10 blur-xl -z-10" />
          <div className="absolute bottom-4 right-4 w-24 h-24 bg-green-200 rounded-full opacity-10 blur-xl -z-10" />
          <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <SectionTitle>
                <span className="inline-flex items-center gap-2">
                  <svg width="32" height="32" fill="none" viewBox="0 0 32 32"><circle cx="16" cy="16" r="16" fill="#3B82F6" opacity="0.15"/><path d="M16 8a8 8 0 100 16 8 8 0 000-16zm0 14.4A6.4 6.4 0 1116 9.6a6.4 6.4 0 010 12.8z" fill="#3B82F6"/></svg>
                  About Somali Eye Care
                </span>
              </SectionTitle>
              <div className="relative h-60 md:h-72 overflow-y-auto overflow-x-visible rounded-xl bg-white shadow-lg ring-1 ring-blue-100 backdrop-blur-md transition-all duration-300 hover:shadow-2xl">
                <div className="px-6 py-4">
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    Somali Eye Care is a non-governmental, non-profit organization making eye care accessible and sustainable for everyone in Somalia across cities and rural areas regardless of background or income.
                  </p>
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    We deliver eye care through an integrated system: the Somali Eye Care Hospital provides diagnosis, treatment, and sight-restoring surgery; the Somali Eye Care Institute builds capacity through structured education, hands-on training, and research; Somali Eye Care Outreach extends services to underserved communities by raising awareness, conducting screenings, providing basic treatment, and guiding referrals; Somali Eye Care Optical improves access to affordable eyeglasses; and Somali Eye Care Vision Centres bring primary eye care closer to people’s homes with screening, refraction, basic treatment, and timely referral to the Hospital. The Somali Eye Care Eye Bank supports corneal transplantation; because Somalia does not yet have a national eye-donation program, we receive donor corneas from accredited international partners, preserve the tissue safely, and coordinate on-time delivery for surgery, while we build public awareness and the systems needed for future local donation.
                  </p>
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    Millions of Somalis live with preventable or treatable blindness. Our mission and vision are to change that by building local capacity and ensuring every person’s right to sight. We believe no one should lose vision from a treatable condition, and every day we work to deliver sustainable, people-centered solutions to eye care in Somalia.
                  </p>
                </div>
              </div>
              <Link href="/about" className="inline-block bg-gradient-to-r from-blue-500 to-green-500 text-white font-semibold px-8 py-3 rounded-full shadow-lg hover:scale-105 hover:shadow-2xl transition-transform mt-6">
                Learn More
              </Link>
            </div>
            <div className="flex justify-center animate-fade-in">
              <div className="bg-white rounded-2xl shadow-2xl p-4 md:p-8 flex flex-col items-center ring-1 ring-blue-100 backdrop-blur-md">
                <Image
                  src="/assets/img/dsc1.jpg"
                  alt="Eye Care Outreach"
                  width={400}
                  height={280}
                  className="rounded-xl shadow-md mb-4 transition-transform duration-300 hover:scale-105"
                />
                <span className="text-blue-600 font-bold text-lg">Empowering Vision, Changing Lives</span>
              </div>
            </div>
          </div>
        </section>

        {/* About/Beginnings Section (replaces BackgroundSection) */}
        <AboutBeginningsSection />

        {/* Call to Action Section */}
        <CallToActionSection />

        {/* Volunteer Section */}
        <VolunteerSection />

        {/* Awareness Campaigns Section */}
        <AwarenessCampaignsSection />

        {/* Research and Achievements Section */}
        {/* <ResearchAchievementsSection /> */}

        {/* Gallery Section */}
        <GallerySection />

        {/* Contact Section */}
        <section className="container mx-auto px-6 py-16">
          <div className="bg-blue-700 rounded shadow p-4 flex flex-col md:flex-row items-center justify-between text-white">
            <div className="mb-2 md:mb-0">
              <h2 className="text-base font-bold mb-1">Have Questions?</h2>
              <p className="text-sm">Contact Somali Eye Care for more information or support.</p>
            </div>
            <Link href="/contact" className="bg-white text-blue-700 font-semibold px-4 py-2 rounded shadow hover:bg-blue-100 flex items-center gap-2 text-xs">
              <FaEnvelope className="text-base" /> Contact Us
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// Add to global CSS (e.g. globals.css):
// @layer utilities {
//   .animate-scroll-text {
//     animation: scrollText 18s linear infinite;
//   }
//   @keyframes scrollText {
//     0% { transform: translateY(100%); }
//     100% { transform: translateY(-100%); }
//   }
// }
